<template>
  <div>
    <h1>Diary</h1>
    <v-btn
      v-if="$keycloak.hasRealmRole('root')"
      @click="updateAutoIncrements"
      small
      >Update auto increments</v-btn
    >
    <div style="text-align: right">
      <span>
        Last updated: {{ lastUpdated | formatDateTimeSeconds }}
        <a @click="refreshAll">Refresh</a>
      </span>
    </div>
    <div v-if="callbacks && callbacks.length">
      <h2 id="quoteDiaryCallbacks">
        Callbacks {{ countFormatted(callbacks) }}
      </h2>
      <table border="1">
        <tr>
          <th>Id</th>
          <th>Name</th>
          <th>Total</th>
          <th>Cat</th>
          <th>Telephone</th>
          <th>Time</th>
          <th />
        </tr>
        <tr v-for="quote in callbacks" :key="quote.id">
          <td>
            <QuoteZ :quote="quote" />
          </td>
          <td>{{ quote.name }}</td>
          <td :class="quote.totalClass">
            {{ quote.total | toCurrency }}
          </td>
          <td>
            <span v-if="quote.quoteType">{{
              quote.quoteType.abbreviation
            }}</span>
            <span v-if="!quote.quoteType">{{ quote.quotetypeid }}</span>
          </td>
          <td>
            <Telephone
              :value="quote.telephone"
              @onCopy="onCopy()"
              @onError="onError()"
            />
          </td>
          <td>{{ quote.timestamp | formatDateTime }}</td>
          <td><a @click="updateCallback(quote)">Dismiss</a></td>
        </tr>
        <tr>
          <td colspan="2">&#160;</td>
          <td>{{ totalFooter(callbacks) | toCurrency }}</td>
          <td colspan="4">&#160;</td>
        </tr>
      </table>
    </div>
    <h2 id="quoteDiaryInstructions">
      Instructions {{ countFormatted(instructions) }}
    </h2>
    <v-progress-circular
      indeterminate
      color="info"
      v-if="$apollo.queries.instructions.loading"
    />
    <v-alert :value="instructions && instructions.length == 0" type="info"
      >No instructions found</v-alert
    >
    <table border="1" v-if="instructions && instructions.length > 0">
      <tr>
        <th>Id</th>
        <th>Name</th>
        <th>Sent</th>
        <th>Total</th>
        <th>Referrer</th>
        <th>Cat</th>
        <th>Username</th>
        <th>Time</th>
        <th>File</th>
      </tr>
      <tr v-for="quote in computedInstructions" :key="quote.id">
        <td>
          <QuoteZ :quote="quote" />
        </td>
        <td :title="quote.emails.addresses">
          <span v-if="quote.addressBooksByType">
            <span
              v-for="client in quote.addressBooksByType"
              :key="client.entity.id"
            >
              <EntityZ :entity="client.entity" />&#160;
            </span>
          </span>
          <span :class="quote.emails.class">{{ quote.name }}</span>
        </td>
        <td :class="quote.logCount > 0 ? 'quoteSent' : 'quoteSentNo'">
          {{ quote.logCount }}
        </td>
        <td :class="quote.totalClass">
          {{ quote.total | toCurrency }}
        </td>
        <td>
          <router-link
            v-if="quote.referrer"
            :to="{ name: 'referrer', params: { id: quote.referrer.id } }"
            >{{ quote.referrer.name }}</router-link
          >
        </td>
        <td>
          <span v-if="quote.quoteType">{{ quote.quoteType.abbreviation }}</span>
        </td>
        <td>
          <EntityZ v-if="quote.user" :entity="quote.user" format="system" />
        </td>
        <td>{{ quote.timestamp | formatTime }}</td>
        <td v-if="quote.files && quote.files.length > 0" class="quoteFile">
          <div v-for="file in quote.files" :key="file.id">
            <FileGql :file="file" />
          </div>
        </td>
        <td v-if="quote.files && quote.files.length == 0" class="quoteFileNo">
          No files
        </td>
        <td v-if="!quote.files">
          <v-progress-circular indeterminate color="info" />
        </td>
      </tr>
      <tr>
        <td colspan="3">&#160;</td>
        <td>{{ totalFooter(instructions) | toCurrency }}</td>
        <td colspan="5">&#160;</td>
      </tr>
    </table>
    <h2 id="quoteDiaryEnquiries">Enquiries {{ countFormatted(enquiries) }}</h2>
    <v-btn
      :to="{ name: 'emailOffers', query: { date: $route.query.date } }"
      v-if="enquiries && enquiries.length"
    >
      <v-icon>email</v-icon>Email offers
    </v-btn>
    <v-progress-circular indeterminate color="info" v-if="!enquiries" />
    <v-alert :value="enquiries && enquiries.length == 0" type="info"
      >No enquiries found</v-alert
    >
    <table border="1" v-if="enquiries && enquiries.length">
      <tr>
        <th>Id</th>
        <th>Name</th>
        <th>Sent</th>
        <th>Total</th>
        <th>Referrer</th>
        <th>Cat</th>
        <th>Telephone</th>
        <th>Username</th>
        <th>Time</th>
      </tr>
      <tr v-for="quote in computedEnquiries" :key="quote.id">
        <td>
          <QuoteZ :quote="quote" />
        </td>
        <td :title="quote.emails.addresses">
          <span v-if="quote.addressBooksByType">
            <span
              v-for="client in quote.addressBooksByType"
              :key="client.entity.id"
            >
              <EntityZ :entity="client.entity" />&#160;
            </span>
          </span>
          <span :class="quote.emails.class">{{ quote.name }}</span>
        </td>
        <td :class="quote.logCount > 0 ? 'quoteSent' : 'quoteSentNo'">
          {{ quote.logCount }}
        </td>
        <td :class="quote.totalClass">
          {{ quote.total | toCurrency }}
        </td>
        <td>
          <router-link
            v-if="quote.referrer"
            :to="{ name: 'referrer', params: { id: quote.referrer.id } }"
            >{{ quote.referrer.name }}</router-link
          >
        </td>
        <td>
          <span v-if="quote.quoteType">{{ quote.quoteType.abbreviation }}</span>
        </td>
        <td>
          <Telephone
            :value="quote.telephone"
            @onCopy="onCopy()"
            @onError="onError()"
          />
          <!-- <span
            v-if="blacklist && blacklist[quote.ipaddress] != undefined"
            :class="blacklist[quote.ipaddress] ? 'ipAddressBlack' : 'ipAddress'"
            >{{ quote.ipaddress }}</span
          >
          <span v-if="!blacklist || blacklist[quote.ipaddress] == undefined">{{
            quote.ipaddress
          }}</span> -->
        </td>
        <td>
          <EntityZ v-if="quote.user" :entity="quote.user" format="system" />
        </td>
        <td :class="quote.completed ? 'quoteCompleted' : 'quoteCompletedNo'">
          {{ quote.timestamp | formatTime }}
          <br />
          <input
            type="checkbox"
            :title="`Call 1 ${quote.call1date}`"
            v-model="quote.call1"
            :disabled="quote.call1"
            @click="updateCall(quote, 1)"
          />
          <input
            type="checkbox"
            :title="`Call 2 ${quote.call2date}`"
            v-model="quote.call2"
            :disabled="!quote.call1 || quote.call2"
            @click="updateCall(quote, 2)"
          />
          <input
            type="checkbox"
            :title="`Call 3 ${quote.call3date}`"
            v-model="quote.call3"
            :disabled="!quote.call2 || quote.call3"
            @click="updateCall(quote, 3)"
          />
          <input
            type="checkbox"
            :title="`Completed ${quote.completeddate}`"
            v-model="quote.completed"
            :disabled="!quote.call3 || quote.completed"
            @click="updateCall(quote, 4)"
          />
        </td>
      </tr>
      <tr>
        <td colspan="3">&#160;</td>
        <td>{{ totalFooter(enquiries) | toCurrency }}</td>
        <td colspan="5">&#160;</td>
      </tr>
    </table>
    <h2 id="quoteDiaryQuotes">Quotes {{ countFormatted(quotes) }}</h2>
    <v-progress-circular indeterminate color="info" v-if="!quotes" />
    <v-alert :value="quotes && quotes.length == 0" type="info"
      >No quotes found</v-alert
    >
    <table border="1" v-if="quotes && quotes.length > 0">
      <tr>
        <th>Id</th>
        <th>Total</th>
        <th>Referrer</th>
        <th>Cat</th>
        <th>Ip</th>
        <th>Username</th>
        <th>Time</th>
        <th>User agent</th>
      </tr>
      <tr v-for="quote in quotes" :key="quote.id">
        <td>
          <QuoteZ :quote="quote" />
        </td>
        <td :class="quote.totalClass">{{ quote.total | toCurrency }}</td>
        <td>
          <router-link
            v-if="quote.referrer"
            :to="{ name: 'referrer', params: { id: quote.referrer.id } }"
            >{{ quote.referrer.name }}</router-link
          >
        </td>
        <td>
          <span v-if="quote.quoteType">{{ quote.quoteType.abbreviation }}</span>
        </td>
        <td>
          <span
            :class="quote.blacklist != null ? 'ipAddressBlack' : 'ipAddress'"
            >{{ quote.ipaddress }}</span
          >
        </td>
        <td>
          <EntityZ v-if="quote.user" :entity="quote.user" format="system" />
        </td>
        <td>{{ quote.timestamp | formatTime }}</td>
        <td>
          <span v-if="quote.userAgent">{{ quote.userAgent.body }}</span>
        </td>
      </tr>
      <tr>
        <td>&#160;</td>
        <td>{{ totalFooter(quotes) | toCurrency }}</td>
        <td colspan="6">&#160;</td>
      </tr>
    </table>
    <v-snackbar
      v-model="snackbar"
      :multi-line="true"
      :timeout="3000"
      :top="true"
    >
      {{ snackbarText }}
      <v-btn color="pink" flat @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import QUOTES_CALLBACK from "@/graphql/sales/diary/QuotesCallback";
import QUOTES_QUOTE from "@/graphql/sales/diary/QuotesQuote";
import QUOTES_ENQUIRY from "@/graphql/sales/diary/QuotesEnquiry";
import QUOTES_INSTRUCTED from "@/graphql/sales/diary/QuotesInstructed";
import UPDATE_QUOTE_CALL from "@/graphql/sales/diary/UpdateQuoteCall";
import UPDATE_QUOTE_CALLBACK from "@/graphql/sales/diary/UpdateQuoteCallback";
import UPDATE_AUTO_INCREMENTS from "@/graphql/sales/UpdateAutoIncrements";
import EntityZ from "@/components/link/EntityZ";
import FileGql from "@/components/link/FileGql";
import QuoteZ from "@/components/link/QuoteZ";
import Telephone from "@/components/quote/extras/Telephone";
import { formatISO } from "date-fns";
export default {
  name: "Diary",
  metaInfo() {
    return {
      title: this.computedTitle,
      titleTemplate: "%s - Sales - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  apollo: {
    callbacks: {
      query: QUOTES_CALLBACK,
      update: (data) => data.quotesCallback,
      // pollInterval: 15000,
      fetchPolicy: "cache-and-network"
    },
    quotes: {
      query: QUOTES_QUOTE,
      variables() {
        return {
          date: this.computedDate
        };
      },
      update: (data) => data.quotesDiary,
      // pollInterval: 15000,
      fetchPolicy: "cache-and-network"
    },
    enquiries: {
      query: QUOTES_ENQUIRY,
      variables() {
        return {
          date: this.computedDate
        };
      },
      update: (data) => data.quotesDiary,
      // pollInterval: 15000,
      fetchPolicy: "cache-and-network"
    },
    instructions: {
      query: QUOTES_INSTRUCTED,
      variables() {
        return {
          date: this.computedDate
        };
      },
      update: (data) => data.quotesDiary,
      // pollInterval: 15000,
      fetchPolicy: "cache-and-network"
    }
  },
  components: { EntityZ, FileGql, Telephone, QuoteZ },
  computed: {
    computedEnquiries() {
      if (!this.enquiries) {
        return undefined;
      }
      return this.enquiries.map((q) => {
        const emails = {
          addresses:
            q.quoteEmails && q.quoteEmails.length
              ? q.quoteEmails.map((qe) => qe.email.address).join(", ")
              : "No email addresses",
          class: q.excludeoffers ? "excludeOffers" : ""
        };
        return { ...q, emails };
      });
    },
    computedInstructions() {
      if (!this.instructions) {
        return undefined;
      }
      return this.instructions.map((q) => {
        const emails = {
          addresses:
            q.quoteEmails && q.quoteEmails.length
              ? q.quoteEmails.map((qe) => qe.email.address).join(", ")
              : "No email addresses",
          class: q.excludeoffers ? "excludeOffers" : ""
        };
        return { ...q, emails };
      });
    },
    computedTitle() {
      return this.$route.query.date
        ? `Diary - ${this.$route.query.date}`
        : "Diary";
    },
    computedDate() {
      return this.$route.query.date
        ? this.$route.query.date.substring(0, 4) +
            "-" +
            this.$route.query.date.substring(4, 6) +
            "-" +
            this.$route.query.date.substring(6, 8)
        : new Date().toISOString().substr(0, 10);
    }
  },
  data() {
    return {
      callbacks: null,
      instructions: null,
      confirmations: null,
      enquiries: null,
      quotes: null,
      lastUpdated: null,
      progressSize: 15,
      toc: [
        {
          id: "quoteDiaryInstructions",
          text: "Instructions",
          target: "#quoteDiaryInstructions"
        },
        {
          id: "quoteDiaryConfirmations",
          text: "Confirmations",
          target: "#quoteDiaryConfirmations"
        },
        {
          id: "quoteDiaryEnquiries",
          text: "Enquiries",
          target: "#quoteDiaryEnquiries"
        },
        {
          id: "quoteDiaryQuotes",
          text: "Quotes",
          target: "#quoteDiaryQuotes"
        },
        {
          id: "quoteDiaryUnsubscribes",
          text: "Unsubscribes",
          target: "#quoteDiaryUnsubscribes"
        }
      ],
      snackbar: false,
      snackbarText: null,
      thedate: null
    };
  },
  methods: {
    countFormatted(quotes) {
      if (!quotes) {
        return 0;
      }
      const saleAndPurchases = quotes.filter((q) => q.quoteType.id === 3);
      if (saleAndPurchases.length) {
        return `${quotes.length + saleAndPurchases.length} (${quotes.length})`;
      } else {
        return quotes.length;
      }
    },
    totalFooter(quotes) {
      return quotes.reduce(function (sum, quote) {
        return sum + quote.total;
      }, 0);
    },
    async updateCall(quote, callid) {
      await this.$apollo.mutate({
        mutation: UPDATE_QUOTE_CALL,
        variables: {
          id: quote.id,
          callid
        },
        refetchQueries: [
          {
            query: QUOTES_ENQUIRY,
            variables: { date: this.computedDate }
          }
        ]
      });
    },
    async updateCallback(quote) {
      if (confirm("Are you sure?")) {
        await this.$apollo.mutate({
          mutation: UPDATE_QUOTE_CALLBACK,
          variables: {
            id: quote.id,
            value: false
          },
          refetchQueries: [{ query: QUOTES_CALLBACK }]
        });
      }
    },
    fetchAll() {
      // this.callbacks = null;
      // this.instructions = null;
      // this.enquiries = null;
      // this.quotes = null;
      this.$store.commit("setTableOfContents", this.toc);
    },
    async updateAutoIncrements() {
      await this.$apollo.mutate({
        mutation: UPDATE_AUTO_INCREMENTS
      });
      alert("done");
    },
    refreshAll() {
      this.$apollo.queries.callbacks.refetch();
      this.$apollo.queries.quotes.refetch();
      this.$apollo.queries.enquiries.refetch();
      this.$apollo.queries.instructions.refetch();
      this.lastUpdated = formatISO(new Date());
    },
    onCopy() {
      this.snackbar = true;
      this.snackbarText = "Copied to clipboard";
    },
    onError() {
      this.snackbar = true;
      this.snackbarText = "Failed to copy texts";
    }
  },
  watch: {
    "$route.query.date": {
      immediate: true,
      handler() {
        this.fetchAll();
      }
    },
    callbacks: {
      immediate: true,
      handler() {
        this.lastUpdated = formatISO(new Date());
      }
    },
    quotes: {
      immediate: true,
      handler() {
        this.lastUpdated = formatISO(new Date());
      }
    },
    enquiries: {
      immediate: true,
      handler() {
        this.lastUpdated = formatISO(new Date());
      }
    },
    instructions: {
      immediate: true,
      handler() {
        this.lastUpdated = formatISO(new Date());
      }
    }
  }
};
</script>

<style scoped>
.quoteAboveLarge {
  color: green;
  font-weight: bold;
}

.quoteAbove {
  color: green;
}

.quoteBelow {
  color: red;
}

.quoteBelowLarge {
  color: red;
  font-weight: bold;
}

.quoteSent {
  background: #ccffcc;
  text-align: center;
  color: gray;
}

.quoteSentNo {
  background: #ffcccc;
  text-align: center;
  color: gray;
}

.excludeOffers {
  color: red;
}

.quoteFile {
  background: #ccffcc;
  white-space: nowrap;
}

.quoteFileNo {
  background: #ffcccc;
  white-space: nowrap;
}

.ipAddress {
  color: green;
}

.ipAddressBlack {
  color: red;
}

.quoteCompleted {
  background: #ccffcc;
}

.quoteCompletedNo {
  background: #ffcccc;
}
</style>
